import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { cn } from '@/utils/cn';
import { parseDateValue } from '@/utils/dateInput';
import { getUiIntlLocale } from '@/i18n/languageContext';
import { CalendarWeekdayHeader } from '@/components/ui/CalendarWeekdayHeader';

export type CalendarPopoverProps = {
  value?: string | null;
  viewMonth: Date;
  onViewMonthChange: (month: Date) => void;
  onSelect: (date: Date) => void;
  min?: string;
  max?: string;
  disabledDates?: string[];
  className?: string;
  /** When true, renders without outer border/shadow for embedding in combined panels. */
  embedded?: boolean;
};

export function CalendarPopover({
  value,
  viewMonth,
  onViewMonthChange,
  onSelect,
  min,
  max,
  disabledDates = [],
  className,
  embedded = false,
}: CalendarPopoverProps) {
  const { t } = useTranslation('common');
  const [viewMode, setViewMode] = useState<'days' | 'months' | 'years'>('days');
  const [yearRangeStart, setYearRangeStart] = useState<number>(
    () => Math.floor(viewMonth.getFullYear() / 12) * 12,
  );

  const selected = parseDateValue(value);
  const minDate = parseDateValue(min);
  const maxDate = parseDateValue(max);
  const blocked = useMemo(
    () => new Set(disabledDates.map((d) => d.slice(0, 10))),
    [disabledDates],
  );

  const locale = getUiIntlLocale();

  // Month names for the current locale
  const monthNames = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(2024, i, 15);
      return new Intl.DateTimeFormat(locale, { month: 'short' }).format(d);
    });
  }, [locale]);

  const monthLongNames = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(2024, i, 15);
      return new Intl.DateTimeFormat(locale, { month: 'long' }).format(d);
    });
  }, [locale]);

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    });
  }, [viewMonth]);

  const isDayDisabled = (day: Date) => {
    if (minDate && day < minDate && !isSameDay(day, minDate)) return true;
    if (maxDate && day > maxDate && !isSameDay(day, maxDate)) return true;
    if (blocked.has(format(day, 'yyyy-MM-dd'))) return true;
    return false;
  };

  const isMonthDisabled = (monthIdx: number) => {
    const year = viewMonth.getFullYear();
    const start = new Date(year, monthIdx, 1);
    const end = endOfMonth(start);
    if (minDate && end < minDate) return true;
    if (maxDate && start > maxDate) return true;
    return false;
  };

  const isYearDisabled = (year: number) => {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    if (minDate && end < minDate) return true;
    if (maxDate && start > maxDate) return true;
    return false;
  };

  const handleSelectMonth = (monthIdx: number) => {
    const next = new Date(viewMonth);
    next.setMonth(monthIdx);
    onViewMonthChange(next);
    setViewMode('days');
  };

  const handleSelectYear = (year: number) => {
    const next = new Date(viewMonth);
    next.setFullYear(year);
    onViewMonthChange(next);
    setViewMode('months');
  };

  const years = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => yearRangeStart + i);
  }, [yearRangeStart]);

  return (
    <div
      className={cn(
        embedded
          ? 'px-3 pt-3'
          : 'rounded-xl border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900',
        className,
      )}
      role={embedded ? undefined : 'dialog'}
      aria-label={embedded ? undefined : t('labels.calendar')}
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between gap-1">
        {viewMode === 'days' && (
          <>
            <button
              type="button"
              className="calendar-nav-btn shrink-0"
              onClick={() => onViewMonthChange(addMonths(viewMonth, -1))}
              aria-label={t('labels.previousMonth')}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </button>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setViewMode('months')}
                className="rounded-md px-2 py-1 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 hover:text-primary-600 dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-primary-400"
                title={t('labels.selectMonth', 'Select month')}
              >
                {monthLongNames[viewMonth.getMonth()]}
              </button>
              <button
                type="button"
                onClick={() => {
                  setYearRangeStart(Math.floor(viewMonth.getFullYear() / 12) * 12);
                  setViewMode('years');
                }}
                className="rounded-md px-2 py-1 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 hover:text-primary-600 dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-primary-400"
                title={t('labels.selectYear', 'Select year')}
              >
                {viewMonth.getFullYear()}
              </button>
            </div>

            <button
              type="button"
              className="calendar-nav-btn shrink-0"
              onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
              aria-label={t('labels.nextMonth')}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </button>
          </>
        )}

        {viewMode === 'months' && (
          <>
            <button
              type="button"
              className="calendar-nav-btn shrink-0"
              onClick={() => onViewMonthChange(addYears(viewMonth, -1))}
              aria-label={t('labels.previousYear', 'Previous Year')}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </button>

            <button
              type="button"
              onClick={() => {
                setYearRangeStart(Math.floor(viewMonth.getFullYear() / 12) * 12);
                setViewMode('years');
              }}
              className="rounded-md px-2 py-1 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 hover:text-primary-600 dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-primary-400"
              title={t('labels.selectYear', 'Select year')}
            >
              {viewMonth.getFullYear()}
            </button>

            <button
              type="button"
              className="calendar-nav-btn shrink-0"
              onClick={() => onViewMonthChange(addYears(viewMonth, 1))}
              aria-label={t('labels.nextYear', 'Next Year')}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </button>
          </>
        )}

        {viewMode === 'years' && (
          <>
            <button
              type="button"
              className="calendar-nav-btn shrink-0"
              onClick={() => setYearRangeStart((prev) => prev - 12)}
              aria-label={t('labels.previousRange', 'Previous 12 Years')}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </button>

            <span className="px-2 py-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
              {yearRangeStart} - {yearRangeStart + 11}
            </span>

            <button
              type="button"
              className="calendar-nav-btn shrink-0"
              onClick={() => setYearRangeStart((prev) => prev + 12)}
              aria-label={t('labels.nextRange', 'Next 12 Years')}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </button>
          </>
        )}
      </div>

      {/* Body Views */}
      {viewMode === 'days' && (
        <>
          <CalendarWeekdayHeader />
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day) => {
              const inMonth = isSameMonth(day, viewMonth);
              const selectedDay = selected ? isSameDay(day, selected) : false;
              const disabled = isDayDisabled(day);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(day)}
                  className={cn(
                    'calendar-day',
                    !inMonth && 'calendar-day--muted',
                    selectedDay && 'calendar-day--selected',
                    isToday(day) && !selectedDay && 'calendar-day--today',
                    disabled && 'calendar-day--disabled',
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </>
      )}

      {viewMode === 'months' && (
        <div className="grid grid-cols-3 gap-2 py-1">
          {monthNames.map((name, idx) => {
            const isCurrentMonth = viewMonth.getMonth() === idx;
            const disabled = isMonthDisabled(idx);
            return (
              <button
                key={name}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectMonth(idx)}
                className={cn(
                  'flex h-11 items-center justify-center rounded-lg text-xs font-semibold transition',
                  isCurrentMonth
                    ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600'
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
                  disabled && 'cursor-not-allowed opacity-30 hover:bg-slate-50 dark:hover:bg-slate-800',
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      {viewMode === 'years' && (
        <div className="grid grid-cols-3 gap-2 py-1">
          {years.map((year) => {
            const isCurrentYear = viewMonth.getFullYear() === year;
            const disabled = isYearDisabled(year);
            return (
              <button
                key={year}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectYear(year)}
                className={cn(
                  'flex h-11 items-center justify-center rounded-lg text-xs font-semibold transition',
                  isCurrentYear
                    ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600'
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
                  disabled && 'cursor-not-allowed opacity-30 hover:bg-slate-50 dark:hover:bg-slate-800',
                )}
              >
                {year}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
